/**
 * SUB-2.11 · FR63. Who reaches the sub-accounts entry point, and what it says.
 *
 * FR63 is a bootstrap requirement: a parent with ZERO sub-accounts must still be
 * able to reach the creation entry point. The account picker cannot be that entry
 * point, because `canSeeTenantPicker` returns false at `tenantCount <= 1` and a
 * parent that has not created its first sub-account reaches exactly one account.
 * So the entry point is a route of its own, and nothing here reads the picker.
 *
 * Three decisions, each with a wrong obvious alternative.
 *
 * 1. "IS THIS A PARENT" IS ANSWERED BY THE SERVER, NOT BY A CLIENT READ.
 *    The gate consumes `sub_account_usage(p_tenant_id)`, which is SECURITY DEFINER
 *    and ISO15-gated: a superadmin may ask about any tenant, anyone else only about
 *    a tenant inside their own subtree, and anything else comes back `invalid`
 *    without saying whether the id exists. Reading `tenants.sub_accounts_enabled`
 *    from the browser would answer the same question through F-13, which is the
 *    policy that currently discloses every tenant row to every authenticated user.
 *    One RPC also carries FR5's usage, so the gate and the count cannot disagree.
 *
 * 2. THE ROLE TERM IS "HOLDS AGENCY ADMIN ANYWHERE", MATCHING THE PICKER.
 *    `TenantContext.isAgencyAdmin` is deliberately not scoped to the current
 *    tenant (see `tenantPicker.ts`), and re-deriving a second, differently scoped
 *    answer here would be a fourth spelling of a question the app already answers.
 *    Scoping is not lost by this: an Agency Admin switched into a client account
 *    fails the `enabled` term instead, because a sub-account can never have
 *    sub-accounts enabled (the one-level trigger, `20260812f`, refuses it).
 *
 * 3. NONE OF THIS IS ENFORCEMENT, AND THE ROUTE MUST STILL BE GUARDED.
 *    SUB-2.2 shipped a sub-accounts card whose only gate was the sidebar entry,
 *    and `/app/settings?tab=accounts` typed by hand showed it to any tenant Admin.
 *    That is the FR19 disclosure SUB-2.12 forbids. So this function guards the
 *    ROUTE as well as the nav row, and the page renders nothing before it answers.
 */

/** The shape `sub_account_usage(uuid)` returns, as `20260812f` builds it. */
export interface SubAccountUsage {
  /** `ok`, or `invalid` for "not yours, or not a tenant" — deliberately one answer. */
  status: 'ok' | 'invalid';
  /** Whether sub-accounts are enabled on the account asked about. */
  enabled: boolean;
  /** Non-archived children today (FR67 excludes archived ones). */
  used: number;
  /** The cap, or null for unlimited. 0 is a real value: FR68 freezes new creation. */
  allowance: number | null;
  unlimited: boolean;
  at_limit: boolean;
}

export interface SubAccountsEntryViewer {
  /** Superadmin, as the app has always measured it. */
  hasSystemOverride: boolean;
  /** Holds the Agency Admin role in at least one account. */
  isAgencyAdmin: boolean;
  /**
   * Both role terms above have actually been fetched.
   *
   * ⚠️ This field exists because of a bug, and deleting it will reintroduce it.
   * Both flags start FALSE while their contexts load, so "not a superadmin" and
   * "we do not know yet" are the same value, and the gate cannot tell them
   * apart. The RPC below is one round trip and usually wins that race, so
   * without this the page decided the viewer had no authority and redirected a
   * superadmin off it. A gate over N asynchronous inputs is closed only when
   * every input has answered.
   */
  rolesResolved: boolean;
}

/**
 * Parse the RPC's jsonb without trusting its shape.
 *
 * The function returns `jsonb_build_object('status','invalid')` alone on refusal,
 * so every other key is genuinely absent on that path rather than null. Treating
 * a missing `enabled` as anything but false is how a refusal turns into an open
 * door, so each field is narrowed independently and the defaults are the closed
 * ones.
 */
export function parseSubAccountUsage(raw: unknown): SubAccountUsage | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;

  const row = raw as Record<string, unknown>;
  if (row.status !== 'ok' && row.status !== 'invalid') return null;

  // A refusal carries no other key, so there is nothing to narrow. Every derived
  // field takes its CLOSED value rather than its absent one.
  //
  // ⚠️ `unlimited: false`, not `allowance === null`. A refusal used to parse into
  // an object claiming an unlimited allowance, because null allowance was the
  // derivation and a refusal has no allowance. `SubAccountUsage` is exported and
  // its consumers did not all check `status`, so the most open value in the type
  // was the one a refusal produced. Same reasoning for `at_limit: true`.
  if (row.status === 'invalid') {
    return {
      status: 'invalid',
      enabled: false,
      used: 0,
      allowance: null,
      unlimited: false,
      at_limit: true,
    };
  }

  // ⚠️ PostgREST renders bigint and numeric as JSON STRINGS. `typeof v ===
  // 'number'` alone therefore turns a real cap into `null`, and `null` is the
  // unlimited sentinel — a column type change would have silently removed the
  // FR4/FR68 block. Coerce what can be coerced, and REFUSE the rest rather than
  // defaulting: on this payload every default that is not a refusal is open.
  const asCount = (v: unknown): number | null => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  };

  const used = asCount(row.used);
  if (used === null || used < 0) return null;

  // Absent is malformed, not unlimited. The `ok` payload always carries the key;
  // `null` is how it says "no cap". Letting an absent key mean unlimited is the
  // same fail-open shape as the coercion above.
  let allowance: number | null;
  if (row.allowance === null) {
    allowance = null;
  } else if (row.allowance === undefined) {
    return null;
  } else {
    allowance = asCount(row.allowance);
    if (allowance === null || allowance < 0) return null;
  }

  return {
    status: 'ok',
    enabled: row.enabled === true,
    used,
    allowance,
    // Derived from `allowance` rather than read, so a null allowance and
    // `unlimited: false` cannot arrive together and be believed.
    unlimited: allowance === null,
    // ⚠️ OR'd with the derivation, not read. `at_limit` is a server field that can
    // contradict `used` and `allowance` — `{used:5, allowance:5, at_limit:false}`
    // parsed cleanly and skipped the cap branch entirely. The same principle that
    // made `unlimited` derived applies here; trusting one and deriving the other
    // was the inconsistency.
    at_limit: row.at_limit === true || (allowance !== null && used >= allowance),
  };
}

/**
 * FR63. The entry point exists for a superadmin or an Agency Admin, in an account
 * that actually has sub-accounts enabled, and at no other time.
 *
 * `usage === null` is the pre-answer state and must read as closed: the page is
 * rendered from this, so an optimistic default would flash the sub-accounts UI at
 * a sub-account's own user for one paint, which is exactly FR19's disclosure.
 */
export function canSeeSubAccountsEntry(
  viewer: SubAccountsEntryViewer,
  usage: SubAccountUsage | null,
): boolean {
  // Closed while the answer is unknown, in BOTH directions: an unanswered RPC
  // must not open it, and unfetched roles must not close it against a viewer who
  // does have the authority. Only the first of those is a disclosure; the second
  // is the one that shipped broken.
  if (!viewer.rolesResolved) return false;
  if (!usage || usage.status !== 'ok' || !usage.enabled) return false;
  return viewer.hasSystemOverride || viewer.isAgencyAdmin;
}

/**
 * FR5. Usage against the allowance, wherever sub-accounts are managed.
 *
 * The two forms are the PRD's own: "4 of 10 sub-accounts", and
 * "4 sub-accounts (unlimited)" when there is no cap.
 *
 * ⚠️ The two forms pluralise on DIFFERENT numbers, which is why this is not one
 * expression. In "1 of 5 sub-accounts" the noun belongs to the allowance; in
 * "1 sub-account (unlimited)" it belongs to the count. Pluralising both on `used`
 * gives "1 of 5 sub-account", and the zero-to-one transition is the first thing
 * the FR63 user ever sees.
 */
export function formatSubAccountUsage(usage: SubAccountUsage): string {
  const plural = (n: number) => (n === 1 ? 'sub-account' : 'sub-accounts');

  // A refusal has no figures. Saying so is the only honest render; the previous
  // version would have printed "0 of null sub-accounts".
  if (usage.status !== 'ok') return 'Sub-account usage unavailable';

  if (usage.unlimited || usage.allowance === null) {
    return `${usage.used} ${plural(usage.used)} (unlimited)`;
  }

  // FR68 lowers an allowance below the current count, so over-cap is a state the
  // product creates deliberately. Rendering it as a plain ratio reads as normal.
  if (usage.used > usage.allowance) {
    return `${usage.used} of ${usage.allowance} ${plural(usage.allowance)} (over the limit)`;
  }

  return `${usage.used} of ${usage.allowance} ${plural(usage.allowance)}`;
}

/**
 * FR4. At the cap, creation is blocked with a message naming the limit and
 * directing the user to Joules. Returned as a reason string rather than a
 * boolean, so the caller cannot show a disabled control with no explanation.
 *
 * ⚠️ Ordering matters. The refusal to answer is checked FIRST, then the cap, so
 * an account whose usage could not be read is never told it is free to create.
 *
 * ⚠️ SUB-3.1: null is now a reachable answer, and it means "no reason the CLIENT
 * can see". It is not permission. Every term here is enforced again server-side
 * by `create_sub_account`, which additionally requires the caller to hold Agency
 * Admin on their own account and refuses a name that collides with one of their
 * existing client accounts. Neither is visible from this payload.
 */
export function subAccountCreationBlockedReason(
  usage: SubAccountUsage,
): string | null {
  // A refusal is not permission. This function used to answer a `status:'invalid'`
  // usage with the cheerful SUB-3.1 string, because it never looked at `status`.
  if (usage.status !== 'ok') {
    return 'This account’s allowance could not be read, so creating a sub-account is blocked. Reload, and contact Joules if it persists.';
  }

  if (usage.at_limit) {
    // `allowance` is non-null whenever `at_limit` was derived, but the type still
    // permits the pair and this branch used to interpolate it raw: an at-limit
    // unlimited account printed "limit of null sub-accounts".
    if (usage.allowance === null) {
      return 'This account has reached its sub-account limit. Contact Joules to raise it.';
    }
    const noun = usage.allowance === 1 ? 'sub-account' : 'sub-accounts';
    return `This account has reached its limit of ${usage.allowance} ${noun}. Contact Joules to raise it.`;
  }
  // SUB-3.1 landed. `create_sub_account` exists, derives the parent from the
  // caller, and is granted to `authenticated`, so below the cap there is no
  // longer a reason to give.
  //
  // The string this used to return named SUB-3.1 as the blocker, and two tests
  // pinned it precisely so that enabling the control would mean deleting a test
  // that says why rather than watching a tautology keep passing. This is that
  // deletion, and the replacement tests assert the null.
  return null;
}
